/**
 * SendWelcomeEmailButton (T074 - US8)
 * 
 * Button component for admin to send welcome emails to professionals.
 * Displays in the provider editor page header.
 * 
 * Features:
 * - Only visible for professionals (SERVICE_PROVIDER, FINANCIAL_ADVISOR)
 * - Shows loading state during email send
 * - Displays success/error toast notifications
 * - Disabled when email is being sent
 */

import { Mail, Loader2 } from 'lucide-react';
import { useSendWelcomeEmail } from '../hooks/useAdminUsers';
import { useToast } from '@/shared/components/Toast';
import type { UserRole } from '../types/admin-user.type';

type SendWelcomeEmailButtonProps = {
  userId: string;
  userRole: UserRole;
  userEmail: string;
};

/**
 * Determine if user role is a professional
 */
function isProfessional(role: UserRole): boolean {
  return role === 'SERVICE_PROVIDER' || role === 'FINANCIAL_ADVISOR';
}

export function SendWelcomeEmailButton({
  userId,
  userRole,
  userEmail,
}: SendWelcomeEmailButtonProps) {
  const { showToast } = useToast();
  const sendWelcomeEmail = useSendWelcomeEmail();

  // Only show button for professionals
  if (!isProfessional(userRole)) {
    return null;
  }

  const handleSendWelcomeEmail = async () => {
    try {
      const result = await sendWelcomeEmail.mutateAsync(userId);
      showToast(result.message, 'success', 4000);
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : 'Failed to send welcome email. Please try again.';
      showToast(errorMessage, 'error', 5000);
    }
  };

  return (
    <button
      type="button"
      onClick={handleSendWelcomeEmail}
      disabled={sendWelcomeEmail.isPending}
      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#026857] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      title={`Send welcome email to ${userEmail}`}
    >
      {sendWelcomeEmail.isPending ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Sending...
        </>
      ) : (
        <>
          <Mail className="mr-2 h-4 w-4" />
          Send Welcome Email
        </>
      )}
    </button>
  );
}
